/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/
import { useEffect, useState } from "react";
import type { MouseEventHandler, ReactNode } from "react";

// @mui material components
import Collapse from "@mui/material/Collapse";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiBadge from "components/VuiBadge";

import { useBadges } from "context/app";

// Custom styles for the SidenavItem
import { item, itemContent, itemArrow } from "examples/Sidenav/styles/sidenavItem";

// Vision UI Dashboard PRO React contexts
import { useVisionUIController } from "context";

// Models
import type { SidenavColor } from "models/theme.model";

interface SidenavItemProps {
  color?: SidenavColor;
  name: string;
  active?: boolean;
  nested?: boolean;
  // The nested items; shows the arrow and the collapse.
  children?: ReactNode;
  open?: boolean;
  // The badges key whose count shows next to the name.
  badgeName?: string;
  onClick?: MouseEventHandler<HTMLLIElement>;
}

function SidenavItem({ color, name, active = false, nested = false, children, open = false, badgeName, ...rest }: SidenavItemProps) {
  const [controller] = useVisionUIController();
  const { miniSidenav } = controller;

  const [badgeData, setBadgeData] = useState<number | null>(null);

  const { badges } = useBadges();

  useEffect(() => {
    if (badges && badgeName && badges[badgeName]) {
      setBadgeData(badges[badgeName])
    }
    
  }, [badges]);

  return (
    <>
      <ListItem {...rest} component="li" sx={item}>
        <VuiBox sx={(theme) => itemContent(theme, { color, active, miniSidenav, name, nested })}>
          <ListItemText primary={name} />
          {badgeData && (
            <VuiBadge badgeContent={badgeData} container color="primary" />
          )}
          {children && (
            <Icon component="i" sx={(theme) => itemArrow(theme, { open, miniSidenav })}>
              expand_less
            </Icon>
          )}
        </VuiBox>
      </ListItem>
      {children && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
}

export default SidenavItem;
